/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from "react";
import { accessAPI, Boton, Titulo } from "../../shared";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";

// Utilitario para ajustar masivamente los precios de las propuestas comerciales
// (y sábanas) aceptadas por un porcentaje. Filtros por cliente y por moneda; las
// propuestas que no cumplen los filtros desaparecen; cada una se puede
// des-seleccionar. Al aplicar, ajusta el precio acordado, que es el que usan
// las próximas facturaciones y refacturaciones de las actividades.
export default function AjustePropuestas({ onClose }) {
  const [loader, setLoader] = useState(true);
  const [aplicando, setAplicando] = useState(false);
  const [monedas, setMonedas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [porcentaje, setPorcentaje] = useState(0);

  // Filtros (todo seleccionado al inicio).
  const [monedasSel, setMonedasSel] = useState(new Set());
  const [clientesSel, setClientesSel] = useState(new Set());
  // Ítems (procesos/sábanas) des-seleccionados individualmente.
  const [deseleccionados, setDeseleccionados] = useState(new Set());

  const toast = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/propuesta/ajuste-masivo",
      null,
      (data) => {
        setMonedas(data.monedas || []);
        setClientes(data.clientes || []);
        setMonedasSel(new Set((data.monedas || []).map((m) => m.id)));
        setClientesSel(new Set((data.clientes || []).map((c) => c.id)));
        setLoader(false);
      },
      (r) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r[0]?.msg || "Error al cargar propuestas",
          sticky: true
        });
      }
    );
  }, []);

  function toggleSet(setState, value) {
    setState((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  // Un ítem es visible si alguna de sus monedas está seleccionada.
  const itemVisible = (item) => item.monedaIds?.some((m) => monedasSel.has(m));

  // Clientes visibles: seleccionados en el filtro y con al menos un ítem visible.
  const clientesVisibles = useMemo(
    () =>
      clientes
        .filter((c) => clientesSel.has(c.id))
        .map((c) => ({
          ...c,
          procesos: c.procesos.filter(itemVisible),
          sabanas: c.sabanas.filter(itemVisible)
        }))
        .filter((c) => c.procesos.length || c.sabanas.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientes, clientesSel, monedasSel]
  );

  const seleccionados = useMemo(() => {
    const propuestaComercialIds = new Set();
    const sabanaIds = new Set();
    for (const c of clientesVisibles) {
      for (const p of c.procesos) {
        if (!deseleccionados.has(`proc-${p.procesoId}`)) {
          propuestaComercialIds.add(p.propuestaComercialId);
        }
      }
      for (const s of c.sabanas) {
        if (!deseleccionados.has(`sab-${s.sabanaId}`)) {
          sabanaIds.add(s.sabanaId);
        }
      }
    }
    return {
      propuestaComercialIds: [...propuestaComercialIds],
      sabanaIds: [...sabanaIds]
    };
  }, [clientesVisibles, deseleccionados]);

  const totalSeleccionados =
    seleccionados.propuestaComercialIds.length +
    seleccionados.sabanaIds.length;

  function aplicar() {
    if (!porcentaje) {
      toast.current?.show({
        severity: "warn",
        summary: "Atención",
        detail: "Ingrese un porcentaje distinto de 0"
      });
      return;
    }
    if (totalSeleccionados === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Atención",
        detail: "No hay propuestas seleccionadas"
      });
      return;
    }
    setAplicando(true);
    accessAPI(
      "PUT",
      "admin/propuesta/ajuste-masivo",
      {
        porcentaje,
        propuestaComercialIds: seleccionados.propuestaComercialIds,
        sabanaIds: seleccionados.sabanaIds,
        monedaIds: [...monedasSel]
      },
      (r) => {
        setAplicando(false);
        toast.current?.show({
          severity: "success",
          summary: "Precios ajustados",
          detail: `${r.preciosAcordados + r.itemsSabana} precio(s) actualizados`,
          life: 4000
        });
        onClose?.();
      },
      (r) => {
        setAplicando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r[0]?.msg || "Error al aplicar el ajuste",
          sticky: true
        });
      }
    );
  }

  if (loader) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <Toast ref={toast} position="top-center" />
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} width="100%" height="2rem" />
        ))}
      </div>
    );
  }

  return (
    <div css={{ marginBottom: "50px" }}>
      <Toast ref={toast} position="top-center" />
      <Titulo>Ajustar precios de propuestas</Titulo>

      <div css={{ marginBottom: "1rem" }}>
        <label
          css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}
        >
          Porcentaje de ajuste
        </label>
        <InputNumber
          value={porcentaje}
          onValueChange={(e) => setPorcentaje(e.value || 0)}
          suffix=" %"
          mode="decimal"
          minFractionDigits={0}
          maxFractionDigits={2}
          inputStyle={{ width: "120px" }}
        />
        <div css={{ color: "var(--grisTextos)", fontSize: "0.85em", marginTop: "0.25em" }}>
          Positivo aumenta, negativo disminuye. Afecta el precio acordado, que
          usan las próximas facturaciones y refacturaciones.
        </div>
      </div>

      <Divider />

      <label css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}>
        Monedas
      </label>
      <div css={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
        {monedas.map((m) => (
          <div key={m.id} css={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Checkbox
              inputId={`mon-${m.id}`}
              checked={monedasSel.has(m.id)}
              onChange={() => toggleSet(setMonedasSel, m.id)}
            />
            <label htmlFor={`mon-${m.id}`} css={{ cursor: "pointer" }}>
              {m.nombre} {m.simbolo ? `(${m.simbolo})` : ""}
            </label>
          </div>
        ))}
      </div>

      <label css={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}>
        Clientes
      </label>
      <MultiSelect
        value={[...clientesSel]}
        options={clientes.map((c) => ({ label: c.razonsocial, value: c.id }))}
        onChange={(e) => setClientesSel(new Set(e.value))}
        filter
        display="chip"
        placeholder="Seleccionar clientes"
        showSelectAll
        css={{ width: "100%", marginBottom: "1rem" }}
      />

      <Divider />

      {clientesVisibles.length === 0 ? (
        <p css={{ color: "var(--grisTextos)" }}>
          No hay propuestas que cumplan los filtros.
        </p>
      ) : (
        clientesVisibles.map((c) => (
          <div key={c.id} css={{ marginBottom: "1.25rem" }}>
            <div
              css={{
                fontWeight: "bold",
                padding: "0.4rem 0",
                borderBottom: "2px solid var(--surface-300)"
              }}
            >
              {c.razonsocial}
            </div>
            {c.procesos.map((p) => {
              const key = `proc-${p.procesoId}`;
              return (
                <div
                  key={key}
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.4rem 0",
                    borderBottom: "1px solid var(--surface-200)"
                  }}
                >
                  <Checkbox
                    inputId={key}
                    checked={!deseleccionados.has(key)}
                    onChange={() => toggleSet(setDeseleccionados, key)}
                  />
                  <label htmlFor={key} css={{ cursor: "pointer" }}>
                    {p.nombre}
                  </label>
                </div>
              );
            })}
            {c.sabanas.map((s) => {
              const key = `sab-${s.sabanaId}`;
              return (
                <div
                  key={key}
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.4rem 0",
                    borderBottom: "1px solid var(--surface-200)"
                  }}
                >
                  <Checkbox
                    inputId={key}
                    checked={!deseleccionados.has(key)}
                    onChange={() => toggleSet(setDeseleccionados, key)}
                  />
                  <label htmlFor={key} css={{ cursor: "pointer" }}>
                    Sábana: {s.nombre}
                  </label>
                </div>
              );
            })}
          </div>
        ))
      )}

      <div css={{ marginTop: "1.5rem" }}>
        <Boton
          verde
          callback={aplicar}
          loader={aplicando}
          confirmacion={`¿Ajustar el precio de ${totalSeleccionados} propuesta(s) en ${porcentaje}%?`}
        >
          Aplicar ajuste ({totalSeleccionados})
        </Boton>
      </div>
    </div>
  );
}
